<template>
  <form class="mb-30 choose-form" @submit.prevent="addFirstResult">
    <div class="form-group mb-25">
      <label class="d-block fs-14 text-black mb-2">Choose Warehouse</label>
      <select
        class="bg-white border-0 rounded-1 fs-14 text-optional"
        v-model.number="selectedWarehouseId"
        :disabled="isWarehouseLocked || loading"
        @change="onWarehouseChange"
      >
        <option :value="null">Choose Warehouse</option>
        <option v-for="warehouse in warehouses" :key="warehouse.id" :value="warehouse.id">
          {{ warehouse.name }}
        </option>
      </select>
      <small v-if="isWarehouseLocked" class="text-muted d-block mt-2">
        Warehouse is locked from the current user profile.
      </small>
    </div>

    <div class="form-group">
      <label class="d-block fs-14 text-black mb-2">Choose Product</label>
      <div class="search-area style-two position-relative w-100 barcode-search">
        <input
          ref="searchInput"
          type="text"
          v-model="productQuery"
          @focus="showResults = true"
          @keyup.enter.prevent="addFirstResult"
          placeholder="Scan / Search product by code or name"
          class="w-100 h-55 bg_ash border-0 rounded-1 fs-14 text-black bg-white pe-5"
          autocomplete="off"
        />
        <button type="button" class="search-btn border-0 bg-transparent" tabindex="-1" aria-hidden="true">
          <img src="../../../assets/img/icons/search.svg" alt="Search" />
        </button>
      </div>
      <div v-if="shouldShowDropdown" class="barcode-search-results mt-2">
        <div class="product-list-header">
          <span>{{ wantsAllProducts ? "Available products" : "Matching products" }}</span>
          <small>{{ visibleProducts.length }} item{{ visibleProducts.length === 1 ? "" : "s" }}</small>
        </div>

        <button
          v-for="product in visibleProducts"
          :key="product.id"
          type="button"
          class="product-result-item"
          @click="addProduct(product)"
        >
          <div class="product-thumb-wrap">
            <img :src="getProductImage(product)" alt="Product" class="product-thumb" />
          </div>

          <div class="product-result-main">
            <strong class="product-result-name">{{ product.name }}</strong>
            <small class="product-result-category">{{ product.category?.name || "No Category" }}</small>
          </div>

          <div class="product-result-meta">
            <span class="product-result-code">{{ product.code }}</span>
            <span class="product-result-price">${{ formatPrice(product.price) }}</span>
          </div>
        </button>

        <div v-if="!visibleProducts.length" class="product-empty-state">
          No products found for this warehouse.
        </div>
      </div>
    </div>
  </form>
</template>

<script lang="ts">
import { computed, defineComponent, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import productService, { type Product } from "@/services/product";
import warehouseService, { type Warehouse } from "@/services/warehouse";
import stateStore from "../../../utils/store";
import { useCurrentUser } from "../../../composables/useCurrentUser";
import defaultProductImg from "../../../assets/img/icons/products.svg";
import { buildStorageAssetUrl } from "../../../utils/settings";

const normalizeSearch = (value: string) =>
  value.replace(/\s+/g, " ").trim().toLowerCase();

export default defineComponent({
  name: "ChooseForm",
  setup() {
    const warehouses = ref<Warehouse[]>([]);
    const selectedWarehouseId = ref<number | null>(null);
    const products = ref<Product[]>([]);
    const productQuery = ref("");
    const loading = ref(false);
    const showResults = ref(false);
    const searchInput = ref<HTMLInputElement | null>(null);
    const { currentUser, fetchCurrentUser } = useCurrentUser();

    const profileWarehouseId = computed(() => {
      const value = currentUser.value?.warehouse_id;
      return value ? Number(value) : null;
    });

    const isWarehouseLocked = computed(() => profileWarehouseId.value !== null);

    const fetchProducts = async () => {
      try {
        products.value = await productService.getProducts({
          warehouse_id: selectedWarehouseId.value || undefined,
        });
      } catch (error) {
        console.error("Failed to load products", error);
      }
    };

    const applyWarehouseSelection = (warehouseId: number | null) => {
      selectedWarehouseId.value = warehouseId;
      const selectedWarehouse = warehouses.value.find((warehouse) => Number(warehouse.id) === Number(warehouseId));
      stateStore.setWarehouse(selectedWarehouse?.id ?? null, selectedWarehouse?.name ?? "");
    };

    const fetchWarehouses = async () => {
      loading.value = true;
      try {
        warehouses.value = await warehouseService.getWarehouses();
        const lockedWarehouseId = profileWarehouseId.value;

        if (lockedWarehouseId) {
          applyWarehouseSelection(lockedWarehouseId);
        } else if (selectedWarehouseId.value === null && warehouses.value.length > 0) {
          applyWarehouseSelection(Number(warehouses.value[0].id));
        } else {
          applyWarehouseSelection(selectedWarehouseId.value);
        }
      } catch (error) {
        console.error("Failed to load warehouses", error);
      } finally {
        loading.value = false;
      }
    };

    const normalizedQuery = computed(() => normalizeSearch(productQuery.value));
    const wantsAllProducts = computed(() => productQuery.value.length > 0 && normalizedQuery.value.length === 0);

    const filteredProducts = computed(() => {
      if (wantsAllProducts.value) {
        return products.value;
      }

      if (!normalizedQuery.value) {
        return [];
      }

      return products.value.filter((product) => {
        const haystackName = normalizeSearch(product.name || "");
        const haystackCode = normalizeSearch(product.code || "");
        return haystackName.includes(normalizedQuery.value) || haystackCode.includes(normalizedQuery.value);
      });
    });

    const visibleProducts = computed(() => filteredProducts.value.slice(0, 12));
    const shouldShowDropdown = computed(() => showResults.value && productQuery.value.length > 0);

    const getProductImage = (product: Product) => {
      const mainAttachment = product.attachments?.find((attachment) => attachment.is_main === 1) || product.attachments?.[0];
      const filePath = mainAttachment?.file_path || mainAttachment?.path;
      if (!filePath) {
        return defaultProductImg;
      }

      return buildStorageAssetUrl(filePath);
    };

    const formatPrice = (price?: number) => Number(price || 0).toFixed(2);

    const addProduct = (product: Product) => {
      stateStore.addSelectedProduct(product);
      stateStore.open = true;
      nextTick(() => searchInput.value?.focus());
    };

    const addFirstResult = () => {
      if (visibleProducts.value.length > 0) {
        addProduct(visibleProducts.value[0]);
      }
    };

    const onWarehouseChange = async () => {
      applyWarehouseSelection(selectedWarehouseId.value);
      await fetchProducts();
    };

    const handleDocumentClick = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (searchInput.value?.contains(target)) return;
      const searchContainer = searchInput.value?.closest(".barcode-search");
      if (searchContainer?.contains(target)) return;
      showResults.value = false;
    };

    watch(profileWarehouseId, async (newWarehouseId) => {
      if (newWarehouseId) {
        applyWarehouseSelection(newWarehouseId);
        await fetchProducts();
      }
    });

    onMounted(async () => {
      await fetchCurrentUser(true);
      await fetchWarehouses();
      await fetchProducts();
      document.addEventListener("click", handleDocumentClick);
    });

    onBeforeUnmount(() => {
      document.removeEventListener("click", handleDocumentClick);
    });

    return {
      warehouses,
      selectedWarehouseId,
      loading,
      productQuery,
      showResults,
      searchInput,
      visibleProducts,
      shouldShowDropdown,
      wantsAllProducts,
      isWarehouseLocked,
      getProductImage,
      formatPrice,
      addProduct,
      addFirstResult,
      onWarehouseChange,
    };
  },
});
</script>

<style lang="scss" scoped>
.barcode-search {
  .search-btn {
    position: absolute;
    top: 50%;
    right: 14px;
    transform: translateY(-50%);
    pointer-events: none;

    img {
      width: 18px;
      height: 18px;
      opacity: 0.7;
    }
  }
}

.barcode-search-results {
  max-height: 340px;
  overflow-y: auto;
  border: 1px solid #e9edf5;
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.1);
  padding: 10px;
}

.product-list-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 8px 12px;
  color: #6b7280;
  font-size: 12px;
  font-weight: 600;
}

.product-result-item {
  display: grid;
  grid-template-columns: 56px minmax(0, 1fr) auto;
  align-items: center;
  gap: 14px;
  width: 100%;
  padding: 12px 14px;
  white-space: normal;
  border: 0;
  background: transparent;
  text-align: left;
  border-radius: 14px;
  margin-bottom: 8px;

  &:hover,
  &:focus {
    background: #f8fbff;
    outline: none;
  }

  &:last-child {
    margin-bottom: 0;
  }
}

.product-thumb-wrap {
  width: 56px;
  height: 56px;
  border-radius: 14px;
  background: #f8fafc;
  border: 1px solid #eef2f7;
  display: flex;
  align-items: center;
  justify-content: space-between;
  overflow: hidden;
}

.product-thumb {
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 8px;
}

.product-result-main {
  min-width: 0;
}

.product-result-name {
  display: block;
  color: #1f2937;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.35;
}

.product-result-category {
  display: block;
  margin-top: 4px;
  color: #6b7280;
  font-size: 12px;
  line-height: 1.3;
}

.product-result-code {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 94px;
  padding: 6px 10px;
  border-radius: 999px;
  background: #f3f6fb;
  color: #4b5563;
  font-size: 12px;
  font-weight: 600;
}

.product-result-meta {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
}

.product-result-price {
  color: #2563eb;
  font-size: 13px;
  font-weight: 700;
}

.product-empty-state {
  padding: 18px 16px;
  color: #6b7280;
  font-size: 13px;
  text-align: center;
}

@media only screen and (max-width: 575px) {
  .product-result-item {
    grid-template-columns: 48px minmax(0, 1fr);
  }

  .product-result-meta {
    grid-column: 1 / -1;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
}
</style>
